import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUploadOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { useTranslation } from '../i18n';
import { PageHeader, Section } from '../components/Section';
import { JobResult } from '../components/JobResult';
import { RecentGenerationsStrip } from '../components/RecentGenerationsStrip';
import { getFingerprintNow } from '../hooks/useFingerprint';
import { useSignupNudge } from '../components/SignupNudge';
import { usePriceQuote } from '../hooks/usePriceQuote';

type UploadResult = { url: string; r2_key: string };

const ASPECTS = ['16:9', '4:3', '1:1', '3:4', '9:16'] as const;
type Aspect = (typeof ASPECTS)[number];

async function uploadImage(file: File): Promise<UploadResult> {
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch('/api/v1/instagram-generator/upload', {
    method: 'POST',
    credentials: 'include',
    body: fd,
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`upload failed: ${res.status} ${body}`);
  }
  return (await res.json()) as UploadResult;
}

function ImageInput({
  label,
  value,
  onChange,
  onClear,
}: {
  label: string;
  value: UploadResult | null;
  onChange: (v: UploadResult) => void;
  onClear: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const handle = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    setError(null);
    try {
      onChange(await uploadImage(f));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {label}
        <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>*</Box>
      </Typography>
      {value ? (
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5, bgcolor: 'action.hover' }}
        >
          <Box
            component="img"
            src={value.url}
            alt={label}
            sx={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}
          />
          <Typography variant="body2" sx={{ flex: 1, fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12 }} noWrap>
            {value.r2_key.split('/').slice(-1)[0]}
          </Typography>
          <IconButton size="small" onClick={onClear}><DeleteIcon fontSize="small" /></IconButton>
        </Stack>
      ) : (
        <Button
          component="label"
          variant="outlined"
          fullWidth
          startIcon={busy ? <CircularProgress size={16} /> : <CloudUploadIcon />}
          sx={{ justifyContent: 'flex-start', py: 2.25, borderStyle: 'dashed', color: 'text.secondary' }}
          disabled={busy}
        >
          {busy ? 'Uploading…' : 'Choose image'}
          <input hidden type="file" accept="image/jpeg,image/png,image/webp" onChange={handle} />
        </Button>
      )}
      {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
    </Box>
  );
}

export default function InstagramGenerator() {
  const t = useTranslation();
  const location = useLocation();
  const remix = (location.state as { remix?: Record<string, unknown> } | null)?.remix ?? null;

  const [image, setImage] = useState<UploadResult | null>(null);
  const [prompt, setPrompt] = useState((remix?.prompt as string | undefined) ?? '');
  const [aspect, setAspect] = useState<Aspect>((remix?.aspect_ratio as Aspect | undefined) ?? '9:16');
  const [minHeight, setMinHeight] = useState<480 | 720 | 1080>((remix?.min_height as 480 | 720 | 1080 | undefined) ?? 720);
  const [batchSize, setBatchSize] = useState((remix?.batch_size as number | undefined) ?? 1);
  const [enrichPrompt, setEnrichPrompt] = useState((remix?.enrich_prompt as boolean | undefined) ?? false);
  const [submitting, setSubmitting] = useState(false);
  const [activityId, setActivityId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = !!image && prompt.trim().length > 0 && !submitting;
  const perImage = usePriceQuote({ endpoint: 'instagram-generator', minHeight });
  const credits = perImage != null ? perImage * batchSize : null;

  const { gateGenerate } = useSignupNudge();

  const handleSubmit = async () => {
    // Anonymous users get the signup nudge instead of a doomed 402.
    if (gateGenerate()) return;
    if (!image) return;
    setSubmitting(true);
    setError(null);
    try {
      const fingerprint = await getFingerprintNow();
      const res = await fetch('/api/v1/instagram-generator/generate-async', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt,
          image_url: image.url,
          image_r2_key: image.r2_key,
          aspect_ratio: aspect,
          min_height: minHeight,
          batch_size: batchSize,
          enrich_prompt: enrichPrompt,
          fingerprint,
        }),
      });
      if (res.status === 402) {
        const body = await res.json();
        throw new Error(`Insufficient credits (need ${body.required}, have ${body.available})`);
      }
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(`submit failed: ${res.status} ${body}`);
      }
      const data = await res.json();
      setActivityId(data.activity_id);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setActivityId(null);
    setImage(null);
    setPrompt('');
  };

  return (
    <Box>
      <PageHeader title={t.instagramGenerator.title} description={t.instagramGenerator.description} />
      <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {activityId ? (
            <Section title={t.instagramGenerator.resultTitle}>
              <Paper sx={{ p: 3 }}>
                <JobResult
                  activityId={activityId}
                  resultType="image"
                  onReset={reset}
                  resetLabel={t.instagramGenerator.newGeneration}
                />
              </Paper>
            </Section>
          ) : (
            <>
              <Section title={t.instagramGenerator.mainCharacter} description={t.instagramGenerator.mainCharacterDescription}>
                <Paper sx={{ p: 3 }}>
                  <Box sx={{ maxWidth: 520 }}>
                    <ImageInput
                      label={t.instagramGenerator.mainCharacter}
                      value={image}
                      onChange={setImage}
                      onClear={() => setImage(null)}
                    />
                  </Box>
                </Paper>
              </Section>

              <Section title={t.instagramGenerator.outputTitle}>
                <Paper sx={{ p: 3 }}>
                  <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                    <TextField
                      select
                      label={t.instagramGenerator.aspectRatio}
                      value={aspect}
                      onChange={(e) => setAspect(e.target.value as Aspect)}
                      size="small"
                      sx={{ minWidth: 160 }}
                    >
                      {ASPECTS.map((a) => (
                        <MenuItem key={a} value={a}>{a}</MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      label={t.instagramGenerator.quality}
                      value={minHeight}
                      onChange={(e) => setMinHeight(Number(e.target.value) as 480 | 720 | 1080)}
                      size="small"
                      sx={{ minWidth: 160 }}
                    >
                      <MenuItem value={480}>480p</MenuItem>
                      <MenuItem value={720}>720p</MenuItem>
                      <MenuItem value={1080}>1080p</MenuItem>
                    </TextField>
                    <TextField
                      select
                      label={t.instagramGenerator.batchSize}
                      value={batchSize}
                      onChange={(e) => setBatchSize(Number(e.target.value))}
                      size="small"
                      sx={{ minWidth: 160 }}
                    >
                      {[1, 2, 3, 4].map((n) => (
                        <MenuItem key={n} value={n}>{n}</MenuItem>
                      ))}
                    </TextField>
                  </Stack>
                </Paper>
              </Section>

              <Section title={t.instagramGenerator.prompt}>
                <Paper sx={{ p: 3 }}>
                  <Stack spacing={2}>
                    <TextField
                      fullWidth
                      placeholder={t.instagramGenerator.promptPlaceholder}
                      multiline
                      minRows={3}
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                    />
                    <FormControlLabel
                      control={<Switch checked={enrichPrompt} onChange={(e) => setEnrichPrompt(e.target.checked)} />}
                      label={t.instagramGenerator.enrichPrompt}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {t.instagramGenerator.enrichPromptHint}
                    </Typography>
                  </Stack>
                </Paper>
              </Section>

              {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

              <Stack direction="row" spacing={1.5}>
                <Button variant="contained" size="large" disabled={!canSubmit} onClick={handleSubmit}>
                  {submitting ? (
                    <CircularProgress size={20} sx={{ color: '#fff' }} />
                  ) : (
                    `${t.instagramGenerator.generate}${credits != null ? ` (${credits} ${t.credits.balanceUnit})` : ''}`
                  )}
                </Button>
              </Stack>
            </>
          )}
        </Box>
        <RecentGenerationsStrip endpoint="instagram-generator" />
      </Box>
    </Box>
  );
}
